import ReceivedReferenceUpload from "./ReceivedReferenceUpload";
import { useEffect, useRef, useState } from "react";
import { ReactComponent as HistoryIcon } from "../../../assets/icons/HistoryIcon.svg";
import CustomSelect from "../../DesignSystem/Dropdowns/CustomSelect";
import NewDateField from "../../DesignSystem/Inputs/NewDateField";
import NewInputField from "../../DesignSystem/Inputs/NewInputField";
import NewInputTextArea from "../../DesignSystem/Inputs/NewTextArea";
import NewMobileNumberInput from "../../DesignSystem/Inputs/NewMobileNumberInput";
import RadioGroup from "../../DesignSystem/Inputs/RadioGroup";
import ToggleButton from "../../DesignSystem/Inputs/ToggleButton";
import PrimaryButton from "../../DesignSystem/Buttons/PrimaryButton";
import SecondaryButton from "../../DesignSystem/Buttons/SecondaryButton";
import AlertBanner from "../../DesignSystem/AlertBanner/AlertBanner";
import ReferenceEmailPreviewModal from "./ReferenceEmailPreviewModal";
import Divider from "../../DesignSystem/Layout/Divider";
import FileUpload from "../../DesignSystem/Upload/FileUpload";
import UploadedFileList from "../../DesignSystem/Upload/UploadedFileList";
import ReferenceEditLogs from "./ReferenceEditLogs";
import {
  isValidEmailList,
  isValidReferencePhone,
} from "./utils/referenceValidation";
import {
  buildDefaultReferenceMessageBody,
  removeReferenceMessageBodySignature,
} from "./utils/referenceMessageBody";
import { toDate } from "./WorkHistoryGantt";
import {
  ACCOUNTANT_REFERENCE_QUESTIONS,
  EDUCATIONAL_REFERENCE_QUESTIONS,
  PERSONAL_REFERENCE_QUESTIONS,
  RECRUITMENT_REFERENCE_STATUSES,
  RECRUITMENT_SELECTABLE_REFERENCE_STATUSES,
  TRADE_REFERENCE_QUESTIONS,
  calculateReferenceFollowUpDate,
  canSendReferenceEmailForStatus,
  getReferenceAutomationMessage,
  getReadyReferenceStatus,
  normalizeYesNoRadioValue,
} from "./constants/recruitment";
import { savePersonalReference } from "../../../backend/Recruitment/Referencing/savePersonalReference";
import { logReferenceAudit } from "../../../backend/Recruitment/Referencing/referenceAuditLog";
import { sendReferenceEmail } from "../../../backend/Recruitment/Referencing/sendReferenceEmail";
import {
  DEFAULT_REFERENCE_CONTACT,
  REFERENCE_CONTACTS,
} from "./constants/referenceContacts";

const STATUS_OPTIONS = RECRUITMENT_SELECTABLE_REFERENCE_STATUSES.map(
  (status) => ({ id: status.id, name: status.label }),
);
const FOLLOW_UP_OPTIONS = [
  { value: "weekly", label: "Weekly" },
  { value: "twice-weekly", label: "Twice a week" },
  { value: "set-date", label: "Set a date" },
];
const PERMISSION_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];
const NOT_REQUIRED_STATUS = "not-required";
const VALIDATION_FIELD_LABELS = Object.freeze({
  fullName: "Full Name",
  companyName: "Company Name",
  datesKnown: "Dates Known",
  tradingFrom: "Trading From",
  tradingTo: "Trading To",
  accountantFrom: "Accountant Since",
  educationFrom: "Education From",
  educationTo: "Education To",
  educationDetails: "Further Details",
  adviserPermission: "Adviser Permission",
  contactNumber: "Contact Number",
  recipients: "To",
  cc: "CC",
  bcc: "BCC",
  messageBody: "Message Body",
  saveConfirmed: "Confirmation checkbox",
});
const initialForm = (
  referee,
  applicantName,
  consentAttachment,
  referenceKind,
) => ({
  referenceKind,
  status: referee?.referenceStatus || "awaiting-adviser",
  referenceSent: toDate(referee?.referenceSent),
  referenceFollowUp:
    toDate(referee?.referenceFollowUp) ||
    calculateReferenceFollowUpDate(
      referee?.followUpTimeframe,
      toDate(referee?.referenceSent),
      toDate(referee?.followUpSetDate),
    ),
  lastFollowedUpOn: toDate(
    referee?.lastFollowedUpOn || referee?.referenceLastFollowedUp,
  ),
  referenceReceived: toDate(referee?.referenceReceived),
  followUpTimeframe: referee?.followUpTimeframe || "weekly",
  followUpSetDate: toDate(referee?.followUpSetDate),
  skipReferenceFollowUp: Boolean(referee?.skipReferenceFollowUp),
  internalAdminNotes: referee?.internalAdminNotes || "",
  fullName:
    referenceKind === "trade"
      ? referee?.tradeName || ""
      : referenceKind === "accountant"
        ? referee?.accountantReferenceName ||
          [referee?.title, referee?.initials, referee?.surname]
            .filter(Boolean)
            .join(" ")
        : referee?.refereeName || "",
  companyName: referee?.companyName || "",
  tradingFrom:
    referee?.tradingFrom ||
    String(referee?.datesOfTradingActivities || "").split(/\s+(?:-|to)\s+/i)[0] ||
    "",
  tradingTo:
    referee?.tradingTo ||
    String(referee?.datesOfTradingActivities || "").split(/\s+(?:-|to)\s+/i)[1] ||
    "",
  datesKnown:
    referenceKind === "trade"
      ? referee?.datesOfTradingActivities || ""
      : referee?.datesKnown || referee?.datesOfUnemployment || "",
  contactNumber:
    referenceKind === "accountant"
      ? referee?.telephone || ""
      : referee?.refereePhoneNumber || "",
  accountantFrom: toDate(referee?.accountantFrom || referee?.from),
  educationFrom: toDate(referee?.educationFrom || referee?.from),
  educationTo: toDate(referee?.educationTo || referee?.to),
  educationDetails: referee?.educationDetails || referee?.furtherDetails || "",
  relationship: referee?.relationshipToReferee || "",
  adviserPermission: normalizeYesNoRadioValue(referee?.adviserPermission),
  recipients:
    referenceKind === "trade"
      ? referee?.tradeEmailAddress || ""
      : referenceKind === "accountant"
        ? referee?.emailAddress || ""
        : referenceKind === "educational"
          ? referee?.educationalEmailAddress || referee?.emailAddress || ""
      : referee?.refereeEmailAddress || "",
  sender:
    referee?.referenceSender?.email ||
    referee?.referenceSender ||
    DEFAULT_REFERENCE_CONTACT.email,
  cc: referee?.referenceCc || "",
  bcc: referee?.referenceBcc || "",
  subject:
    referee?.referenceSubject ||
    `${referenceKind === "trade" ? "Trade" : referenceKind === "accountant" ? "Accountant" : referenceKind === "educational" ? "Educational" : "Personal"} Reference Request for ${applicantName || "Applicant"}`,
  questions:
    (referenceKind === "trade"
      ? referee?.tradeReferenceQuestions
      : referenceKind === "accountant"
        ? referee?.accountantReferenceQuestions
        : referenceKind === "educational"
          ? referee?.educationalReferenceQuestions
        : referee?.personalReferenceQuestions) ||
    [
      ...(referenceKind === "trade"
        ? TRADE_REFERENCE_QUESTIONS
        : referenceKind === "accountant"
          ? ACCOUNTANT_REFERENCE_QUESTIONS
          : referenceKind === "educational"
            ? EDUCATIONAL_REFERENCE_QUESTIONS
          : PERSONAL_REFERENCE_QUESTIONS),
    ],
  additionalQuestion: referee?.additionalReferenceQuestion || "",
  messageBody: referee?.referenceMessageBody || "",
  saveConfirmed: false,
  receivedReferenceAttachments: referee?.receivedReferenceAttachments || [],
  attachments: [
    ...(referee?.referenceAttachments || []).filter(
      (attachment) => attachment?.source !== "reference-consent-form",
    ),
    ...(consentAttachment ? [consentAttachment] : []),
  ],
});

const createInitialForm = (...args) => {
  const form = initialForm(...args);
  return {
    ...form,
    messageBody: removeReferenceMessageBodySignature(
      form.messageBody ||
        buildDefaultReferenceMessageBody({ form, applicantName: args[1] }),
    ),
  };
};

const ReferenceForm = ({
  referee,
  applicantName,
  nationalInsuranceNumber,
  applicationId,
  referenceId,
  sourceKey,
  referenceDetails,
  referenceConsentAttachment,
  onSaved,
  onCancel,
  referenceKind = "personal",
  DetailsFields,
  hasSixYearsEmploymentHistory = false,
  hasCurrentEmployment = false,
  applicationEmailBlocked = false,
  applicationEmailBlockedReason = "",
  referenceSender,
  from,
}) => {
  const [form, setForm] = useState(() =>
    createInitialForm(
      referee,
      applicantName,
      referenceConsentAttachment,
      referenceKind,
    ),
  );
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [saveMessageType, setSaveMessageType] = useState("success");
  const [sendSuccessMessage, setSendSuccessMessage] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isReferenceSaved, setIsReferenceSaved] = useState(
    () => Boolean(referee && Object.keys(referee).length),
  );
  const [areEditLogsOpen, setAreEditLogsOpen] = useState(false);
  const editLogsRef = useRef(null);
  const formTopRef = useRef(null);

  useEffect(() => {
    if (!saveMessage || saveMessageType !== "success") return undefined;
    const timeoutId = window.setTimeout(() => setSaveMessage(""), 5000);
    return () => window.clearTimeout(timeoutId);
  }, [saveMessage, saveMessageType]);

  useEffect(() => {
    setForm(
      createInitialForm(
        referee,
        applicantName,
        referenceConsentAttachment,
        referenceKind,
      ),
    );
    setValidationErrors({});
    setHasUnsavedChanges(false);
    setIsReferenceSaved(Boolean(referee && Object.keys(referee).length));
  }, [referee, applicantName, referenceConsentAttachment, referenceKind]);

  useEffect(() => {
    setSaveMessage("");
    setIsPreviewOpen(false);
  }, [referenceId]);

  useEffect(() => {
    if (!sendSuccessMessage) return undefined;
    const timeoutId = window.setTimeout(() => setSendSuccessMessage(""), 5000);
    return () => window.clearTimeout(timeoutId);
  }, [sendSuccessMessage]);

  const update = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setHasUnsavedChanges(true);
    setValidationErrors((current) =>
      current[field] ? { ...current, [field]: false } : current,
    );
  };

  const handleFollowUpTimeframeChange = (event) => {
    const timeframe = event.target.value;
    setForm((current) => ({
      ...current,
      followUpTimeframe: timeframe,
      status: getReadyReferenceStatus({
        status: current.status,
        adviserPermission: current.adviserPermission,
        followUpTimeframe: timeframe,
      }),
      followUpSetDate: timeframe === "set-date" ? current.followUpSetDate : null,
      referenceFollowUp: calculateReferenceFollowUpDate(
        timeframe,
        current.referenceSent,
        current.followUpSetDate,
      ),
    }));
    setHasUnsavedChanges(true);
  };

  const handleAdviserPermissionChange = (event) => {
    const adviserPermission = event.target.value;
    setForm((current) => ({
      ...current,
      adviserPermission,
      status: getReadyReferenceStatus({
        status: current.status,
        adviserPermission,
        followUpTimeframe: current.followUpTimeframe,
      }),
    }));
    setHasUnsavedChanges(true);
    setValidationErrors((current) => ({ ...current, adviserPermission: false }));
  };

  const handleStatusChange = (event) => {
    const status = event.target.value;
    setForm((current) => ({
      ...current,
      status,
      referenceReceived:
        status === "reference-received"
          ? current.referenceReceived || new Date()
          : current.status === "reference-received"
            ? null
            : current.referenceReceived,
      referenceFollowUp:
        status === "reference-received" || status === NOT_REQUIRED_STATUS
          ? null
          : current.referenceFollowUp,
    }));
    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    const isReferenceNotRequired = form.status === NOT_REQUIRED_STATUS;
    const nextErrors = {
      fullName:
        !isReferenceNotRequired &&
        referenceKind !== "educational" &&
        !form.fullName?.trim(),
      companyName:
        !isReferenceNotRequired &&
        referenceKind === "trade" &&
        !form.companyName?.trim(),
      datesKnown:
        !isReferenceNotRequired &&
        referenceKind === "personal" &&
        !String(form.datesKnown || "").trim(),
      tradingFrom:
        !isReferenceNotRequired &&
        referenceKind === "trade" &&
        !String(form.tradingFrom || "").trim(),
      tradingTo:
        !isReferenceNotRequired &&
        referenceKind === "trade" &&
        !String(form.tradingTo || "").trim(),
      accountantFrom:
        !isReferenceNotRequired &&
        referenceKind === "accountant" &&
        !form.accountantFrom,
      educationFrom:
        !isReferenceNotRequired &&
        referenceKind === "educational" &&
        !form.educationFrom,
      educationTo:
        !isReferenceNotRequired &&
        referenceKind === "educational" &&
        !form.educationTo,
      educationDetails:
        !isReferenceNotRequired &&
        referenceKind === "educational" && !form.educationDetails?.trim(),
      adviserPermission: !isReferenceNotRequired && !form.adviserPermission,
      contactNumber:
        !isReferenceNotRequired && !isValidReferencePhone(form.contactNumber),
      recipients: isReferenceNotRequired
        ? ""
        : !form.recipients?.trim()
        ? "At least one recipient is required."
        : !isValidEmailList(form.recipients, { required: true })
          ? "Enter valid email addresses separated by commas."
          : "",
      cc: !isValidEmailList(form.cc)
        ? "Enter valid email addresses separated by commas."
        : "",
      bcc: !isValidEmailList(form.bcc)
        ? "Enter valid email addresses separated by commas."
        : "",
      messageBody: isReferenceNotRequired
        ? ""
        : !form.messageBody?.trim()
        ? "Message Body is required."
        : "",
      saveConfirmed: !form.saveConfirmed,
    };
    const hasErrors = Object.values(nextErrors).some(Boolean);
    setValidationErrors(nextErrors);
    if (hasErrors) {
      if (!form.saveConfirmed) {
        void logReferenceAudit({
          applicationId,
          referenceId,
          referenceKind,
          action: "save_confirmation_missing",
          changes: [{
            fieldPath: "confirmations.detailsConfirmedBeforeSave",
            previousValue: false,
            newValue: false,
          }],
          details: { saveAttempted: true },
        }).catch((auditError) => console.error("Reference audit failed", auditError));
      }
      setSaveMessage("");
      requestAnimationFrame(() => {
        formTopRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
      return false;
    }

    setIsSaving(true);
    setSaveMessage("");
    setSaveMessageType("success");
    try {
      const result = await savePersonalReference({
        applicationId,
        referenceId,
        sourceKey,
        referenceDetails,
        referee,
        form,
        referenceKind,
      });
      const savedReferee = result.savedReferee;
      onSaved?.(result.updatedReferees);
      setForm(
        createInitialForm(
          savedReferee,
          applicantName,
          referenceConsentAttachment,
          referenceKind,
        ),
      );
      setSaveMessage(
        `${referenceKind === "trade" ? "Trade" : referenceKind === "accountant" ? "Accountant" : referenceKind === "educational" ? "Educational" : "Personal"} reference saved successfully.`,
      );
      setSaveMessageType("success");
      requestAnimationFrame(() => {
        formTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
      setHasUnsavedChanges(false);
      setIsReferenceSaved(true);
      return true;
    } catch (error) {
      setSaveMessage(error?.message || "Unable to save personal reference.");
      setSaveMessageType("error");
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setForm(
      createInitialForm(
        referee,
        applicantName,
        referenceConsentAttachment,
        referenceKind,
      ),
    );
    setValidationErrors({});
    setSaveMessage("");
    setIsPreviewOpen(false);
    setHasUnsavedChanges(false);
    setIsReferenceSaved(Boolean(referee && Object.keys(referee).length));
    onCancel?.();
  };

  const handleReferenceEmailSent = async () => {
    const result = await sendReferenceEmail({
      applicationId,
      referenceId,
      followUpTimeframe: form.followUpTimeframe || "",
      sender: form.sender || referenceSender || from,
    });
    const sentAt = result.referenceSent ? new Date(result.referenceSent) : new Date();
    const nextFollowUp = result.referenceFollowUp
      ? new Date(result.referenceFollowUp)
      : null;
    setForm((current) => ({
      ...current,
      referenceSent: sentAt,
      referenceFollowUp: nextFollowUp,
      skipReferenceFollowUp: false,
      status: result.status || "reference-sent",
      saveConfirmed: false,
    }));
    setHasUnsavedChanges(false);
    setIsReferenceSaved(true);
    setIsPreviewOpen(false);
    const acceptedRecipientText = result.acceptedRecipients?.join(", ");
    setSendSuccessMessage(
      `Reference email sent successfully${acceptedRecipientText ? ` to ${acceptedRecipientText}` : ""}.`,
    );
    requestAnimationFrame(() => {
      formTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    const savedReferee = {
      ...(referee || {}),
      referenceStatus: result.status || "reference-sent",
      referenceSent: sentAt,
      referenceFollowUp: nextFollowUp,
      skipReferenceFollowUp: false,
    };
    if (referenceKind === "accountant") {
      onSaved?.({ ...referenceDetails, ...savedReferee });
    } else {
      const refereeField =
        referenceKind === "trade"
          ? "tradeReferees"
          : referenceKind === "educational"
            ? "educationalReferees"
            : "personalReferees";
      const currentReferees = referenceDetails?.[refereeField] || [];
      const updatedReferees = Array.isArray(currentReferees)
        ? [...currentReferees]
        : { ...currentReferees };
      updatedReferees[sourceKey] = savedReferee;
      onSaved?.(updatedReferees);
    }
  };

  const systemStatus = RECRUITMENT_REFERENCE_STATUSES.find(
    (status) => status.id === form.status && status.selectable === false,
  );
  const incompleteMandatoryFields = Object.entries(validationErrors)
    .filter(([, error]) => Boolean(error))
    .map(([field]) => VALIDATION_FIELD_LABELS[field] || field);
  const referenceTypeLabel =
    referenceKind === "trade"
      ? "Trade Reference"
      : referenceKind === "accountant"
        ? "Accountant Reference"
      : referenceKind === "educational"
        ? "Educational Reference"
        : "Personal Reference";
  const isReferenceNotRequired = form.status === NOT_REQUIRED_STATUS;
  const hasViewRequestErrors = Boolean(
    isReferenceNotRequired ||
    (referenceKind !== "educational" && !form.fullName?.trim()) ||
      (referenceKind === "trade" && !form.companyName?.trim()) ||
      (referenceKind === "personal" && !String(form.datesKnown || "").trim()) ||
      (referenceKind === "trade" &&
        (!String(form.tradingFrom || "").trim() ||
          !String(form.tradingTo || "").trim())) ||
      (referenceKind === "accountant" && !form.accountantFrom) ||
      (referenceKind === "educational" &&
        (!form.educationFrom || !form.educationTo || !form.educationDetails?.trim())) ||
      !form.adviserPermission ||
      !isValidReferencePhone(form.contactNumber) ||
      !isValidEmailList(form.recipients, { required: true }) ||
      !isValidEmailList(form.cc) ||
      !isValidEmailList(form.bcc) ||
      !form.messageBody?.trim(),
  );

  const handleViewRequest = () => setIsPreviewOpen(true);

  const showEditLogs = () => {
    setAreEditLogsOpen(true);
    requestAnimationFrame(() => {
      editLogsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  return (
    <div className="fit-column gap-l flex-1">
    <div ref={formTopRef} className="work-history-reference-form fit-column gap-l flex-1 bg-white p-m corner card-corner-radius">
      <AlertBanner
        type="info"
        message={getReferenceAutomationMessage(form)}
        showClose={false}
      />
      {saveMessage && (
        <AlertBanner type={saveMessageType} message={saveMessage} showClose={false} />
      )}
      {incompleteMandatoryFields.length > 0 && (
        <AlertBanner
          type="error"
          message={`Please complete or correct: ${incompleteMandatoryFields.join(", ")}.`}
          showClose={false}
        />
      )}
      {sendSuccessMessage && (
        <AlertBanner
          type="success"
          message={sendSuccessMessage}
          showClose={false}
        />
      )}
      {hasSixYearsEmploymentHistory && (
        <AlertBanner
          type="warning"
          message={`Please note: Employment history covering six years is available for this applicant. ${referenceTypeLabel} can be marked as not required.`}
          showClose={false}
        />
      )}
      {!hasSixYearsEmploymentHistory && (
        <AlertBanner
          type="warning"
          message={
            hasCurrentEmployment
              ? `Please note: The applicant is currently employed at a firm. ${referenceTypeLabel} may not be required.`
              : `Please note: Employment history covering six years is not available for this applicant. ${referenceTypeLabel} may be required.`
          }
          showClose={false}
        />
      )}
      <div className="reference-status-card fit-column gap-m bg-blue-200 p-m corner card-corner-radius">
        <div className="reference-status-card-header display-flex justify-end">
          <button
            type="button"
            className="reference-log-icon-button"
            onClick={showEditLogs}
            aria-label="Show reference logs"
            title="Logs"
          >
            <HistoryIcon />
          </button>
        </div>
        <div className="grid3 gap-m fit-column">
          <CustomSelect
            label="Status of Reference"
            options={STATUS_OPTIONS}
            value={form.status}
            selectedLabel={systemStatus?.label}
            onChange={handleStatusChange}
            clearable={false}
            searchable={false}
          />
          <NewDateField
            label="Date Reference Requested"
            value={form.referenceSent}
            readOnly
            disabled
          />
          <NewDateField
            label="Date Reference Received"
            value={form.referenceReceived}
            onChange={(event) => update("referenceReceived", event.target.value)}
          />
        </div>
        <div className="grid3 gap-m">
          <div className="flex-start-column gap-xs">
            <div className="FP_Content">Set Follow Ups Timeframe</div>
            {FOLLOW_UP_OPTIONS.map((option) => (
              <RadioGroup
                key={option.value}
                name={`${referenceKind}FollowUpTimeframe`}
                options={[option]}
                value={form.followUpTimeframe}
                onChange={handleFollowUpTimeframeChange}
                deselectable
              />
            ))}
            {form.followUpTimeframe === "set-date" && (
              <NewDateField
                value={form.followUpSetDate}
                onChange={(event) => {
                  update("followUpSetDate", event.target.value);
                  update("referenceFollowUp", event.target.value);
                }}
              />
            )}
          </div>
          <div className="flex-start-column gap-xs">
            <NewDateField
              label="Date of Next Follow Up"
              value={form.referenceFollowUp}
              readOnly
              disabled
            />
            <div className="display-flex align-center gap-xs">
              <ToggleButton
                checked={form.skipReferenceFollowUp}
                onChange={(checked) => update("skipReferenceFollowUp", checked)}
              />
              <div className="FP_Content">Skip next follow up</div>
            </div>
          </div>
          <NewDateField
            label="Last Followed Up On"
            value={form.lastFollowedUpOn}
            readOnly
            disabled
          />
        </div>
        {form.status === "reference-received" && (
          <ReceivedReferenceUpload files={form.receivedReferenceAttachments} onChange={(files) => update("receivedReferenceAttachments", files)} />
        )}
        <NewInputTextArea
          label="Internal Admin Notes"
          value={form.internalAdminNotes}
          onChange={(event) => update("internalAdminNotes", event.target.value)}
          maxLength={2000}
        />
      </div>

      <div className="FP_Body_Semibold text-black">Details for Reference Email</div>
      {DetailsFields ? (
        <DetailsFields
          form={form}
          update={update}
          validationErrors={validationErrors}
          isReferenceNotRequired={isReferenceNotRequired}
        />
      ) : referenceKind === "trade" ? (
        <>
        <NewInputField
          label="Type of Reference"
          value="Trade Reference"
          disabled
        />
        <div className="grid2 gap-m">
          <NewInputField label="Full Name" value={form.fullName} onChange={(event) => update("fullName", event.target.value)} required={!isReferenceNotRequired} error={validationErrors.fullName ? "Full Name is required." : ""} />
          <NewInputField label="Company Name" value={form.companyName} onChange={(event) => update("companyName", event.target.value)} required={!isReferenceNotRequired} error={validationErrors.companyName ? "Company Name is required." : ""} />
        </div>
        <div className="grid2 gap-m">
          <NewMobileNumberInput label="Contact Number" value={form.contactNumber} onChange={(value) => update("contactNumber", value)} error={validationErrors.contactNumber ? "Enter a valid telephone number." : ""} />
          <div className="grid2 gap-s">
            <NewInputField
              label="Trading From"
              value={form.tradingFrom}
              onChange={(event) => update("tradingFrom", event.target.value)}
              placeholder="YYYY"
              required={!isReferenceNotRequired}
              error={validationErrors.tradingFrom ? "Trading From is required." : ""}
            />
            <NewInputField
              label="Trading To"
              value={form.tradingTo}
              onChange={(event) => update("tradingTo", event.target.value)}
              placeholder="YYYY"
              required={!isReferenceNotRequired}
              error={validationErrors.tradingTo ? "Trading To is required." : ""}
            />
          </div>
        </div>
        <RadioGroup name="tradeAdviserPermission" label="Adviser’s permission to contact for reference" options={PERMISSION_OPTIONS} value={form.adviserPermission} onChange={handleAdviserPermissionChange} required={!isReferenceNotRequired} inline error={validationErrors.adviserPermission} deselectable />
        </>
      ) : referenceKind === "personal" ? (
        <>
          <NewInputField
            label="Type of Reference"
            value="Personal Reference"
            disabled
          />
          <div className="grid2 gap-m">
          <NewInputField label="Full Name" value={form.fullName} onChange={(event) => update("fullName", event.target.value)} required={!isReferenceNotRequired} error={validationErrors.fullName ? "Full Name is required." : ""} />
          <NewInputField label="Relationship to Referee" value={form.relationship} onChange={(event) => update("relationship", event.target.value)} />
          </div>
          <div className="grid2 gap-m">
          <NewInputField label="Dates Known" value={form.datesKnown} onChange={(event) => update("datesKnown", event.target.value)} required={!isReferenceNotRequired} error={validationErrors.datesKnown ? "This date information is required." : ""} />
          <NewMobileNumberInput label="Contact Number" value={form.contactNumber} onChange={(value) => update("contactNumber", value)} error={validationErrors.contactNumber ? "Enter a valid telephone number." : ""} />
          </div>
        <RadioGroup name={`${referenceKind}AdviserPermission`} label="Adviser’s permission to contact for reference" options={PERMISSION_OPTIONS} value={form.adviserPermission} onChange={handleAdviserPermissionChange} required={!isReferenceNotRequired} inline error={validationErrors.adviserPermission} deselectable />
        </>
      ) : (
        <>
          <NewInputField label="Type of Reference" value="Accountant Reference" disabled />
          <div className="grid2 gap-m">
            <NewInputField label="Full Name" value={form.fullName} onChange={(event) => update("fullName", event.target.value)} required={!isReferenceNotRequired} error={validationErrors.fullName ? "Full Name is required." : ""} />
            <NewDateField
              label="Accountant Since"
              value={form.accountantFrom}
              onChange={(event) => update("accountantFrom", event.target.value)}
              required={!isReferenceNotRequired}
              error={validationErrors.accountantFrom}
              helperText={validationErrors.accountantFrom ? "Accountant Since is required." : ""}
            />
          </div>
          <NewMobileNumberInput label="Contact Number" value={form.contactNumber} onChange={(value) => update("contactNumber", value)} error={validationErrors.contactNumber ? "Enter a valid telephone number." : ""} />
          <RadioGroup name="accountantAdviserPermission" label="Adviser’s permission to contact for reference" options={PERMISSION_OPTIONS} value={form.adviserPermission} onChange={handleAdviserPermissionChange} required={!isReferenceNotRequired} inline error={validationErrors.adviserPermission} deselectable />
        </>
      )}
      <Divider />
      <CustomSelect
        label="From"
        options={REFERENCE_CONTACTS}
        value={form.sender}
        onChange={(event) => update("sender", event.target.value)}
        clearable={false}
        searchable={false}
      />
      <div className="grid2 gap-m">
        <NewInputField label="To" value={form.recipients} onChange={(event) => update("recipients", event.target.value)} required={!isReferenceNotRequired} error={validationErrors.recipients || ""} />
        <NewInputField label="Subject" value={form.subject} onChange={(event) => update("subject", event.target.value)} />
        <NewInputField label="CC" value={form.cc} onChange={(event) => update("cc", event.target.value)} error={validationErrors.cc || ""} />
        <NewInputField label="BCC" value={form.bcc} onChange={(event) => update("bcc", event.target.value)} error={validationErrors.bcc || ""} />
      </div>
      <div className="fit-column gap-s">
        <FileUpload
          label="Attachments"
          description="Drag & drop reference documents here or"
          browseText="Browse"
          accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
          multiple
          files={form.attachments.filter((file) => file?.source !== "reference-consent-form")}
          onFilesChange={(files) => {
            const selectedFiles = Array.isArray(files) ? files : [files];
            update("attachments", [
              ...form.attachments,
              ...selectedFiles.filter(Boolean),
            ]);
          }}
        />
        <UploadedFileList
          files={form.attachments.filter((file) => file?.source !== "reference-consent-form")}
          canRemove={(file) => file?.source !== "reference-consent-form"}
          onRemove={(file) =>
            update("attachments", form.attachments.filter((attachment) => attachment !== file))
          }
        />
      </div>
      <NewInputTextArea
        label="Message Body"
        value={form.messageBody}
        onChange={(event) => update("messageBody", event.target.value)}
        placeholder="Edit the email template message body"
        maxLength={5000}
        className="reference-message-body-field"
        required={!isReferenceNotRequired}
        error={validationErrors.messageBody || ""}
      />
      <div className="reference-sticky-actions fit-column gap-m">
      <label className={`reference-save-confirmation display-flex align-center gap-s FP_Content ${validationErrors.saveConfirmed ? "reference-save-confirmation--error" : ""}`}>
        <input
          type="checkbox"
          checked={form.saveConfirmed}
          onChange={(event) => update("saveConfirmed", event.target.checked)}
        />
        <span>I confirm that all reference details are correct before saving.</span>
      </label>
      <div className="row-space-between gap-m">
        <div className="display-flex gap-s">
          <SecondaryButton label={isSaving ? "Saving..." : "Save"} disabled={isSaving} onClick={handleSave} />
          <SecondaryButton label="Cancel" disabled={isSaving} onClick={handleCancel} />
        </div>
        <PrimaryButton type="button" label="View Request" disabled={hasViewRequestErrors} onClick={handleViewRequest} />
      </div>
      </div>
      <ReferenceEmailPreviewModal
        open={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        onSend={handleReferenceEmailSent}
        form={form}
        applicantName={applicantName}
        nationalInsuranceNumber={nationalInsuranceNumber}
        history={referee}
        applicationId={applicationId}
        referenceId={referenceId}
        referenceKind={referenceKind}
        needsSave={!isReferenceSaved || hasUnsavedChanges}
        isSaving={isSaving}
        onSave={handleSave}
        onSaveConfirmationChange={(checked) => update("saveConfirmed", checked)}
        canSend={
          !applicationEmailBlocked &&
          isReferenceSaved &&
          !hasUnsavedChanges &&
          canSendReferenceEmailForStatus(form.status)
        }
        sendDisabledReason={
          applicationEmailBlocked
            ? applicationEmailBlockedReason
            : !isReferenceSaved
            ? "Save this reference before sending the email."
            : hasUnsavedChanges
              ? "You have unsaved changes. Close this preview and save the reference before sending."
              : !canSendReferenceEmailForStatus(form.status)
                ? "The current reference status does not allow emails to be sent."
                : ""
        }
      />
    </div>
      <ReferenceEditLogs
        applicationId={applicationId}
        referenceId={referenceId}
        open={areEditLogsOpen}
        onToggle={() => setAreEditLogsOpen((current) => !current)}
        sectionRef={editLogsRef}
      />
    </div>
  );
};

export default ReferenceForm;
